import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../../../../../../shared/services/auth.service';
import {
  FetchSitesListRequest,
  SettingsUpdateBidItemSiteQuantityRequest,
} from '../../../../../../../core/projects.actions';
import { sitesListInfo } from '../../../../../../../core/projects.selectors';

@Component({
  standalone: false,
  selector: 'app-bid-items-site-quantities',
  templateUrl: './bid-items-site-quantities.component.html',
  styleUrls: ['./bid-items-site-quantities.component.scss'],
})
export class BidItemsSiteQuantitiesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() data: any;
  @Input() projectId: string | null = null;

  canEdit = false;
  sitesList: any[] = [];
  sitesForm = new FormGroup({});
  private readonly onDestroy = new Subject<void>();

  constructor(
    private store: Store,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.updateCanEdit();
    const id = this.projectId ?? this.data?.projectId ?? this.data?.project_id ?? null;
    if (!id) {
      this.sitesList = [];
      return;
    }
    this.store.dispatch(
      FetchSitesListRequest({
        payload: { projectId: id, qp: { start: 0, total: 100000 } },
      })
    );
    this.store
      .select(sitesListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        const list = info?.list ?? [];
        this.sitesList = list.map((site: any) => ({ id: site?.id, name: site?.name }));
        this.buildFormFromSitesAndData();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['projectId']) {
      this.updateCanEdit();
    }
    const dataChange = changes['data'];
    const projectIdChange = changes['projectId'];
    if (projectIdChange && !projectIdChange.firstChange) {
      const id = this.projectId ?? this.data?.projectId ?? this.data?.project_id ?? null;
      if (id) {
        this.store.dispatch(
          FetchSitesListRequest({
            payload: { projectId: id, qp: { start: 0, total: 100000 } },
          })
        );
      } else {
        this.sitesList = [];
        this.sitesForm = new FormGroup({});
      }
      return;
    }
    if (dataChange && this.sitesList?.length) {
      this.buildFormFromSitesAndData();
    }
    if ((changes['data'] || changes['projectId']) && this.sitesForm?.controls && Object.keys(this.sitesForm.controls).length) {
      if (this.canEdit) {
        this.sitesForm.enable();
      } else {
        this.sitesForm.disable();
      }
    }
  }

  private updateCanEdit(): void {
    const account = this.authService.getCurrentUser();
    const projectId = this.projectId ?? this.data?.projectId ?? this.data?.project_id ?? null;
    const projectRole = account?.roles?.length
      ? account.roles.find((r: any) => r.project_id === projectId)?.role ?? account?.role
      : account?.role;
    this.canEdit = projectRole === 'org-admin' || projectRole === 'project-manager';
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  save(site: any): void {
    if (!this.canEdit) return;
    const bidItemId = this.data?.id;
    if (!bidItemId || !site?.id) return;
    const control = this.sitesForm?.get(site?.id);
    const raw = control?.value;
    const qty = raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : 1;
    this.store.dispatch(
      SettingsUpdateBidItemSiteQuantityRequest({
        bidItemId,
        siteId: site?.id,
        quantity: qty,
      })
    );
  }

  private buildFormFromSitesAndData(): void {
    const siteQuantities = this.data?.site_quantities ?? [];
    const group: any = {};
    this.sitesList.forEach((site: any) => {
      const sq = siteQuantities.find((r: any) => r?.site_id === site?.id);
      const value = sq != null && sq?.quantity != null ? Number(sq?.quantity) : null;
      group[site?.id] = new FormControl(value);
    });
    this.sitesForm = new FormGroup(group);
    if (!this.canEdit) {
      this.sitesForm.disable();
    }
  }
}
